/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { WarningIcon } from '@chakra-ui/icons';
import {
  Button,
  Center,
  Code,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { FallbackProps } from 'react-error-boundary';
import { ButtonVariant } from '@/themes/ButtonVariants';
import { FallbackRender } from '@sentry/react';

interface ErrorFallbackProps {
  error: unknown;
  resetError: () => void;
  message: string;
  size: 'small' | 'medium' | 'large';
  errorModalHeader: string;
}

export function UnifiedErrorFallback({
  error,
  resetError,
  message = 'Something Went Wrong',
  size = 'large',
  errorModalHeader = message,
}: ErrorFallbackProps) {
  const disclosure = useDisclosure();
  const handleReset = () => {
    resetError();
    disclosure.onClose();
  };

  let errorMessage = 'Could not find the error message';
  if (error instanceof Error) {
    errorMessage = error.message;
  } else if (error && typeof error === 'object') {
    errorMessage = JSON.stringify(error, null, 2);
  } else if (typeof error === 'string') {
    errorMessage = error;
  }

  return (
    <Center height="100%">
      <VStack>
        <HStack>
          <WarningIcon fontSize={size} color="red.500" />
          <Text color="red.500" fontWeight="medium" fontSize={size}>
            {message}
          </Text>
        </HStack>
        <Button variant={ButtonVariant.secondary} onClick={disclosure.onOpen}>
          Know More
        </Button>
      </VStack>
      <Modal {...disclosure}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{errorModalHeader}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Code width="100%">
              <pre style={{ whiteSpace: 'pre-wrap' }}>{errorMessage}</pre>
            </Code>
          </ModalBody>
          <ModalFooter>
            <Button variant={ButtonVariant.secondary} onClick={handleReset}>
              Reset Error
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Center>
  );
}

// Wrapper for React Error Boundary
export function ErrorBoundaryFallbackBuilder(
  message: string,
  size: 'small' | 'medium' | 'large',
  errorModalHeader: string = message,
) {
  return (props: FallbackProps) => (
    <UnifiedErrorFallback
      error={props.error}
      resetError={props.resetErrorBoundary}
      message={message}
      size={size}
      errorModalHeader={errorModalHeader}
    />
  );
}

// Wrapper for Sentry Error Boundary
export const SentryErrorFallback: FallbackRender = ({ error, resetError }) => (
  <UnifiedErrorFallback
    error={error}
    resetError={resetError}
    message="Something Went Wrong"
    size="large"
    errorModalHeader="Error Details"
  />
);
